import React, { useState, useEffect, useRef } from "react";
import {
  supabase,
  Preparation,
  PreparationIngredient,
  Patient,
  Prescriber,
  Ingredient,
  Tables,
  User,
} from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { logAuditEvent } from "../utils/auditLog";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  PrinterIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

interface PreparationWithDetails extends Preparation {
  operator_name?: string;
  validator_name?: string;
  ingredients?: PreparationIngredient[];
}

const CompoundingPreparations: React.FC = () => {
  const { selectedPharmacy, user } = useAuth();
  const [preparations, setPreparations] = useState<PreparationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPreparation, setEditingPreparation] = useState<Preparation | null>(null);
  const [selectedPreparation, setSelectedPreparation] = useState<PreparationWithDetails | null>(null);
  
  // Autocomplete data
  const [patients, setPatients] = useState<Patient[]>([]);
  const [prescribers, setPrescribers] = useState<Prescriber[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  
  // Form state
  const [formData, setFormData] = useState({
    preparation_type: "personalized" as "bulk" | "personalized",
    internal_lot_number: "",
    batch_number: "",
    preparation_form: "capsules" as Preparation["preparation_form"],
    amount: "",
    amount_unit: "g",
    expiry_date: "",
    preparation_date: new Date().toISOString().split("T")[0],
    operator_id: user?.id || "",
    patient_id: "",
    patient_name: "",
    prescriber_id: "",
    prescriber_name: "",
    posology: "",
    notes: "",
  });

  // Ingredient form state
  const [ingredientForm, setIngredientForm] = useState({
    ingredient_id: "",
    ingredient_name: "",
    internal_lot_number: "",
    theoretical_amount: "",
    actual_amount: "",
    unit: "g",
    unit_dose: "", // Dose per unit (eenheidsdosis)
    unit_dose_unit: "mg", // Unit for unit dose
    number_of_units: "", // Number of capsules/suppositories
  });

  const [preparationIngredients, setPreparationIngredients] = useState<
    Omit<PreparationIngredient, "id" | "preparation_id" | "created_at" | "updated_at">[]
  >([]);

  // Autocomplete states
  const [patientSearch, setPatientSearch] = useState("");
  const [prescriberSearch, setPrescriberSearch] = useState("");
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);
  const [showPrescriberSuggestions, setShowPrescriberSuggestions] = useState(false);
  const [showIngredientSuggestions, setShowIngredientSuggestions] = useState(false);

  const patientInputRef = useRef<HTMLInputElement>(null);
  const prescriberInputRef = useRef<HTMLInputElement>(null);
  const ingredientInputRef = useRef<HTMLInputElement>(null);
  const patientDropdownRef = useRef<HTMLDivElement>(null);
  const prescriberDropdownRef = useRef<HTMLDivElement>(null);
  const ingredientDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedPharmacy) {
      fetchData();
      fetchAutocompleteData();
    }
  }, [selectedPharmacy]);

  // Click outside handlers for autocomplete dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        patientDropdownRef.current &&
        !patientDropdownRef.current.contains(event.target as Node) &&
        patientInputRef.current &&
        !patientInputRef.current.contains(event.target as Node)
      ) {
        setShowPatientSuggestions(false);
      }
      if (
        prescriberDropdownRef.current &&
        !prescriberDropdownRef.current.contains(event.target as Node) &&
        prescriberInputRef.current &&
        !prescriberInputRef.current.contains(event.target as Node)
      ) {
        setShowPrescriberSuggestions(false);
      }
      if (
        ingredientDropdownRef.current &&
        !ingredientDropdownRef.current.contains(event.target as Node) &&
        ingredientInputRef.current &&
        !ingredientInputRef.current.contains(event.target as Node)
      ) {
        setShowIngredientSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    if (!selectedPharmacy) return;
    setLoading(true);
    try {
      const { data: prepData, error: prepError } = await supabase
        .from(Tables.PREPARATIONS)
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("preparation_date", { ascending: false })
        .limit(100);

      if (prepError) throw prepError;

      // Fetch ingredients for each preparation
      const preparationsWithIngredients = await Promise.all(
        (prepData || []).map(async (prep) => {
          const { data: ingredients } = await supabase
            .from(Tables.PREPARATION_INGREDIENTS)
            .select("*")
            .eq("preparation_id", prep.id)
            .order("order_index");

          // Fetch operator and validator names
          const operator = users.find((u) => u.id === prep.operator_id);
          const validator = prep.validator_id
            ? users.find((u) => u.id === prep.validator_id)
            : null;

          return {
            ...prep,
            ingredients: ingredients || [],
            operator_name: operator?.full_name,
            validator_name: validator?.full_name,
          };
        })
      );

      setPreparations(preparationsWithIngredients);
    } catch (error) {
      console.error("Error fetching preparations:", error);
      toast.error("Fout bij ophalen van bereidingen");
    } finally {
      setLoading(false);
    }
  };

  const fetchAutocompleteData = async () => {
    if (!selectedPharmacy) return;
    try {
      // Fetch patients
      const { data: patientsData } = await supabase
        .from(Tables.PATIENTS)
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("name");

      // Fetch prescribers
      const { data: prescribersData } = await supabase
        .from(Tables.PRESCRIBERS)
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("name");

      // Fetch ingredients
      const { data: ingredientsData } = await supabase
        .from(Tables.INGREDIENTS)
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("name");

      // Fetch users
      const { data: usersData } = await supabase
        .from(Tables.USERS)
        .select("*");

      setPatients(patientsData || []);
      setPrescribers(prescribersData || []);
      setIngredients(ingredientsData || []);
      setUsers(usersData || []);
    } catch (error) {
      console.error("Error fetching autocomplete data:", error);
    }
  };

  const getNextLotNumber = async () => {
    if (!selectedPharmacy) return "";
    try {
      const { data, error } = await supabase.rpc("get_next_lot_number", {
        p_pharmacy_id: selectedPharmacy.id,
      });
      if (error) throw error;
      return data || "";
    } catch (error) {
      console.error("Error getting lot number:", error);
      // Fallback: generate simple lot number
      const year = new Date().getFullYear().toString().slice(-2);
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, "0");
      return `LOT-${year}-${random}`;
    }
  };

  const getNextBatchNumber = async () => {
    if (!selectedPharmacy) return "";
    try {
      const { data, error } = await supabase.rpc("get_next_batch_number", {
        p_pharmacy_id: selectedPharmacy.id,
      });
      if (error) throw error;
      return data || "";
    } catch (error) {
      console.error("Error getting batch number:", error);
      // Fallback
      const year = new Date().getFullYear();
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, "0");
      return `BATCH-${year}-${random}`;
    }
  };

  const handleNewPreparation = async () => {
    if (!selectedPharmacy || !user) return;
    
    const lotNumber = await getNextLotNumber();
    const batchNumber = formData.preparation_type === "bulk" ? await getNextBatchNumber() : "";

    setFormData({
      ...formData,
      internal_lot_number: lotNumber,
      batch_number: batchNumber,
      operator_id: user.id,
      preparation_date: new Date().toISOString().split("T")[0],
    });
    setPreparationIngredients([]);
    setEditingPreparation(null);
    setShowForm(true);
  };

  const handleAddIngredient = () => {
    if (!ingredientForm.ingredient_name || !ingredientForm.internal_lot_number) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    // Calculate theoretical amount if unit dose and number of units are provided
    let theoreticalAmount = parseFloat(ingredientForm.theoretical_amount) || 0;
    let unitDose: number | undefined;
    let unitDoseUnit: string | undefined;
    let numberOfUnits: number | undefined;

    // For capsules and suppositories, calculate from unit dose
    if (
      (formData.preparation_form === "capsules" || formData.preparation_form === "suppositoiries") &&
      ingredientForm.unit_dose &&
      ingredientForm.number_of_units
    ) {
      unitDose = parseFloat(ingredientForm.unit_dose);
      unitDoseUnit = ingredientForm.unit_dose_unit;
      numberOfUnits = parseInt(ingredientForm.number_of_units);
      theoreticalAmount = unitDose * numberOfUnits;
      
      // Convert unit dose unit to main unit if needed
      if (unitDoseUnit === "mg" && ingredientForm.unit === "g") {
        theoreticalAmount = theoreticalAmount / 1000;
      } else if (unitDoseUnit === "g" && ingredientForm.unit === "mg") {
        theoreticalAmount = theoreticalAmount * 1000;
      }
    }

    if (theoreticalAmount === 0 && !ingredientForm.theoretical_amount) {
      toast.error("Vul theoretische hoeveelheid in of gebruik eenheidsdosis");
      return;
    }

    const newIngredient: Omit<PreparationIngredient, "id" | "preparation_id" | "created_at" | "updated_at"> = {
      ingredient_id: ingredientForm.ingredient_id || undefined,
      ingredient_name: ingredientForm.ingredient_name,
      internal_lot_number: ingredientForm.internal_lot_number,
      theoretical_amount: theoreticalAmount,
      actual_amount: parseFloat(ingredientForm.actual_amount) || 0,
      unit: ingredientForm.unit,
      unit_dose: unitDose,
      unit_dose_unit: unitDoseUnit,
      number_of_units: numberOfUnits,
      order_index: preparationIngredients.length,
    };

    setPreparationIngredients([...preparationIngredients, newIngredient]);
    setIngredientForm({
      ingredient_id: "",
      ingredient_name: "",
      internal_lot_number: "",
      theoretical_amount: "",
      actual_amount: "",
      unit: "g",
      unit_dose: "",
      unit_dose_unit: "mg",
      number_of_units: "",
    });
    setIngredientSearch("");
  };

  const handleRemoveIngredient = (index: number) => {
    setPreparationIngredients(preparationIngredients.filter((_, i) => i !== index));
  };

  const handleSavePreparation = async () => {
    if (!selectedPharmacy || !user) return;

    if (!formData.internal_lot_number || !formData.preparation_form || !formData.expiry_date) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    if (preparationIngredients.length === 0) {
      toast.error("Voeg minimaal één ingrediënt toe");
      return;
    }

    if (formData.preparation_type === "personalized" && !formData.patient_name) {
      toast.error("Vul patiëntnaam in voor gepersonaliseerde bereiding");
      return;
    }

    try {
      // Save or update patient if new
      let patientId = formData.patient_id;
      if (formData.patient_name && !patientId) {
        const existingPatient = patients.find(
          (p) => p.name.toLowerCase() === formData.patient_name.toLowerCase()
        );
        if (existingPatient) {
          patientId = existingPatient.id;
        } else {
          const { data: newPatient, error: patientError } = await supabase
            .from(Tables.PATIENTS)
            .insert({
              pharmacy_id: selectedPharmacy.id,
              name: formData.patient_name,
            })
            .select()
            .single();
          if (patientError) throw patientError;
          patientId = newPatient.id;
          setPatients([...patients, newPatient]);
        }
      }

      // Save or update prescriber if new
      let prescriberId = formData.prescriber_id;
      if (formData.prescriber_name && !prescriberId) {
        const existingPrescriber = prescribers.find(
          (p) => p.name.toLowerCase() === formData.prescriber_name.toLowerCase()
        );
        if (existingPrescriber) {
          prescriberId = existingPrescriber.id;
        } else {
          const { data: newPrescriber, error: prescriberError } = await supabase
            .from(Tables.PRESCRIBERS)
            .insert({
              pharmacy_id: selectedPharmacy.id,
              name: formData.prescriber_name,
            })
            .select()
            .single();
          if (prescriberError) throw prescriberError;
          prescriberId = newPrescriber.id;
          setPrescribers([...prescribers, newPrescriber]);
        }
      }

      // Save or update ingredients if new
      for (const ing of preparationIngredients) {
        if (!ing.ingredient_id && ing.ingredient_name) {
          const existingIngredient = ingredients.find(
            (i) => i.name.toLowerCase() === ing.ingredient_name.toLowerCase()
          );
          if (!existingIngredient) {
            const { data: newIngredient, error: ingError } = await supabase
              .from(Tables.INGREDIENTS)
              .insert({
                pharmacy_id: selectedPharmacy.id,
                name: ing.ingredient_name,
              })
              .select()
              .single();
            if (!ingError && newIngredient) {
              setIngredients([...ingredients, newIngredient]);
            }
          }
        }
      }

      // Save preparation
      const preparationData: any = {
        pharmacy_id: selectedPharmacy.id,
        preparation_type: formData.preparation_type,
        internal_lot_number: formData.internal_lot_number,
        batch_number: formData.batch_number || null,
        preparation_form: formData.preparation_form,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        amount_unit: formData.amount_unit,
        expiry_date: formData.expiry_date,
        preparation_date: formData.preparation_date,
        operator_id: user.id,
        status: "completed",
        patient_id: patientId || null,
        patient_name: formData.patient_name || null,
        prescriber_id: prescriberId || null,
        prescriber_name: formData.prescriber_name || null,
        posology: formData.posology || null,
        notes: formData.notes || null,
      };

      let preparationId: string;

      if (editingPreparation) {
        const { error } = await supabase
          .from(Tables.PREPARATIONS)
          .update(preparationData)
          .eq("id", editingPreparation.id);
        if (error) throw error;
        preparationId = editingPreparation.id;

        // Delete old ingredients
        await supabase
          .from(Tables.PREPARATION_INGREDIENTS)
          .delete()
          .eq("preparation_id", preparationId);
      } else {
        const { data, error } = await supabase
          .from(Tables.PREPARATIONS)
          .insert(preparationData)
          .select()
          .single();
        if (error) throw error;
        preparationId = data.id;
      }

      // Save ingredients
      const ingredientsToInsert = preparationIngredients.map((ing, index) => {
        const existingIngredient = ingredients.find(
          (i) => i.name.toLowerCase() === ing.ingredient_name.toLowerCase()
        );
        return {
          preparation_id: preparationId,
          ingredient_id: existingIngredient?.id || ing.ingredient_id || null,
          ingredient_name: ing.ingredient_name,
          internal_lot_number: ing.internal_lot_number,
          theoretical_amount: ing.theoretical_amount,
          actual_amount: ing.actual_amount,
          unit: ing.unit,
          unit_dose: ing.unit_dose || null,
          unit_dose_unit: ing.unit_dose_unit || null,
          number_of_units: ing.number_of_units || null,
          order_index: index,
        };
      });

      const { error: ingError } = await supabase
        .from(Tables.PREPARATION_INGREDIENTS)
        .insert(ingredientsToInsert);

      if (ingError) throw ingError;

      toast.success("Bereiding opgeslagen");
      setShowForm(false);
      fetchData();
    } catch (error: any) {
      console.error("Error saving preparation:", error);
      toast.error(error.message || "Fout bij opslaan van bereiding");
    }
  };

  const handleValidate = async (preparation: Preparation) => {
    if (!user) return;

    // Check if user is pharmacist or admin
    if (user.role !== "admin" && user.role !== "pharmacist") {
      toast.error("Alleen apothekers kunnen bereidingen valideren");
      return;
    }

    try {
      const { error } = await supabase
        .from(Tables.PREPARATIONS)
        .update({
          validator_id: user.id,
          validated_at: new Date().toISOString(),
          status: "validated",
        })
        .eq("id", preparation.id);

      if (error) throw error;
      toast.success("Bereiding gevalideerd");

      if (selectedPharmacy) {
        await logAuditEvent({
          pharmacy_id: selectedPharmacy.id,
          user_id: user.id,
          user_name: user.full_name,
          action: "validated",
          entity_type: "preparation",
          entity_id: preparation.id,
          entity_label: preparation.internal_lot_number,
        });
      }

      fetchData();
    } catch (error: any) {
      console.error("Error validating preparation:", error);
      toast.error(error.message || "Fout bij valideren");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Weet je zeker dat je deze bereiding wilt verwijderen?")) return;

    try {
      const { error } = await supabase
        .from(Tables.PREPARATIONS)
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Bereiding verwijderd");
      fetchData();
    } catch (error: any) {
      console.error("Error deleting preparation:", error);
      toast.error(error.message || "Fout bij verwijderen");
    }
  };

  const handleEdit = (preparation: PreparationWithDetails) => {
    setFormData({
      preparation_type: preparation.preparation_type,
      internal_lot_number: preparation.internal_lot_number,
      batch_number: preparation.batch_number || "",
      preparation_form: preparation.preparation_form,
      amount: preparation.amount?.toString() || "",
      amount_unit: preparation.amount_unit || "g",
      expiry_date: preparation.expiry_date,
      preparation_date: preparation.preparation_date,
      operator_id: preparation.operator_id,
      patient_id: preparation.patient_id || "",
      patient_name: preparation.patient_name || "",
      prescriber_id: preparation.prescriber_id || "",
      prescriber_name: preparation.prescriber_name || "",
      posology: preparation.posology || "",
      notes: preparation.notes || "",
    });
    setPreparationIngredients(
      preparation.ingredients?.map((ing) => ({
        ingredient_id: ing.ingredient_id || undefined,
        ingredient_name: ing.ingredient_name,
        internal_lot_number: ing.internal_lot_number,
        theoretical_amount: ing.theoretical_amount,
        actual_amount: ing.actual_amount,
        unit: ing.unit,
        unit_dose: ing.unit_dose,
        unit_dose_unit: ing.unit_dose_unit,
        number_of_units: ing.number_of_units,
        order_index: ing.order_index,
      })) || []
    );
    setEditingPreparation(preparation);
    setShowForm(true);
  };

  const handlePrint = (preparation: PreparationWithDetails) => {
    // Create a printable version
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const ingredientsHtml = (preparation.ingredients || [])
      .map(
        (ing, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${ing.ingredient_name}</td>
        <td>${ing.internal_lot_number}</td>
        <td>${ing.theoretical_amount} ${ing.unit}</td>
        <td>${ing.actual_amount} ${ing.unit}</td>
      </tr>
    `
      )
      .join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Weegfiche - ${preparation.internal_lot_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .info { margin: 10px 0; }
            .label { font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Weegfiche - Magistrale Bereiding</h1>
          <div class="info">
            <span class="label">Intern lotnummer:</span> ${preparation.internal_lot_number}
          </div>
          ${preparation.batch_number ? `<div class="info"><span class="label">Batch nummer:</span> ${preparation.batch_number}</div>` : ""}
          <div class="info">
            <span class="label">Type:</span> ${preparation.preparation_type === "bulk" ? "Bulk" : "Gepersonaliseerd"}
          </div>
          <div class="info">
            <span class="label">Vorm:</span> ${preparation.preparation_form}
          </div>
          <div class="info">
            <span class="label">Hoeveelheid:</span> ${preparation.amount || "N/A"} ${preparation.amount_unit || ""}
          </div>
          <div class="info">
            <span class="label">Vervaldatum:</span> ${new Date(preparation.expiry_date).toLocaleDateString("nl-NL")}
          </div>
          <div class="info">
            <span class="label">Bereidingsdatum:</span> ${new Date(preparation.preparation_date).toLocaleDateString("nl-NL")}
          </div>
          <div class="info">
            <span class="label">Operator:</span> ${preparation.operator_name || "N/A"}
          </div>
          ${preparation.patient_name ? `<div class="info"><span class="label">Patiënt:</span> ${preparation.patient_name}</div>` : ""}
          ${preparation.prescriber_name ? `<div class="info"><span class="label">Voorschrijver:</span> ${preparation.prescriber_name}</div>` : ""}
          ${preparation.posology ? `<div class="info"><span class="label">Posologie:</span> ${preparation.posology}</div>` : ""}
          ${preparation.validator_name ? `<div class="info"><span class="label">Gevalideerd door:</span> ${preparation.validator_name} op ${new Date(preparation.validated_at!).toLocaleDateString("nl-NL")}</div>` : ""}
          
          <h2>Ingrediënten</h2>
          <table>
            <thead>
              <tr>
                <th>Nr</th>
                <th>Ingrediënt</th>
                <th>Intern lotnummer</th>
                <th>Theoretisch</th>
                <th>Werkelijk</th>
              </tr>
            </thead>
            <tbody>
              ${ingredientsHtml}
            </tbody>
          </table>
          
          ${preparation.notes ? `<div class="info"><span class="label">Opmerkingen:</span> ${preparation.notes}</div>` : ""}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredPatients = patients.filter((p) =>
    p.name.toLowerCase().includes(patientSearch.toLowerCase())
  );
  const filteredPrescribers = prescribers.filter((p) =>
    p.name.toLowerCase().includes(prescriberSearch.toLowerCase())
  );
  const filteredIngredients = ingredients.filter((i) =>
    i.name.toLowerCase().includes(ingredientSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">
          Magistrale Bereidingen
        </h1>
        <button
          onClick={handleNewPreparation}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nieuwe Bereiding
        </button>
      </div>

      {/* Preparations List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lotnummer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vorm
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Datum
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Operator
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acties
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {preparations.map((prep) => (
              <tr key={prep.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {prep.internal_lot_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {prep.preparation_type === "bulk" ? "Bulk" : "Gepersonaliseerd"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {prep.preparation_form}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(prep.preparation_date).toLocaleDateString("nl-NL")}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {prep.operator_name || "N/A"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {prep.status === "validated" ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Gevalideerd
                    </span>
                  ) : prep.status === "completed" ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      Voltooid
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Concept
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePrint(prep)}
                      className="text-primary-600 hover:text-primary-900"
                      title="Print"
                    >
                      <PrinterIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleEdit(prep)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Bewerken"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {prep.status !== "validated" && (
                      <button
                        onClick={() => handleValidate(prep)}
                        className="text-green-600 hover:text-green-900"
                        title="Valideren"
                      >
                        <CheckCircleIcon className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(prep.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Verwijderen"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {preparations.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Geen bereidingen gevonden
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                {editingPreparation ? "Bereiding Bewerken" : "Nieuwe Bereiding"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4 max-h-[80vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    value={formData.preparation_type}
                    onChange={(e) => {
                      const type = e.target.value as "bulk" | "personalized";
                      setFormData({
                        ...formData,
                        preparation_type: type,
                        batch_number: type === "bulk" ? formData.batch_number : "",
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="personalized">Gepersonaliseerd</option>
                    <option value="bulk">Bulk</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Intern Lotnummer *
                  </label>
                  <input
                    type="text"
                    value={formData.internal_lot_number}
                    onChange={(e) =>
                      setFormData({ ...formData, internal_lot_number: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                {formData.preparation_type === "bulk" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Batch Nummer
                    </label>
                    <input
                      type="text"
                      value={formData.batch_number}
                      onChange={(e) =>
                        setFormData({ ...formData, batch_number: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vorm *
                  </label>
                  <select
                    value={formData.preparation_form}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        preparation_form: e.target.value as Preparation["preparation_form"],
                      })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="capsules">Capsules</option>
                    <option value="suppositoiries">Suppositoiries</option>
                    <option value="siroop">Siroop</option>
                    <option value="creme">Creme</option>
                    <option value="suspensie">Suspensie</option>
                    <option value="zalf">Zalf</option>
                    <option value="oplossing">Oplossing</option>
                    <option value="poeder">Poeder</option>
                    <option value="other">Anders</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hoeveelheid
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      step="0.001"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      className="flex-1 border border-gray-300 rounded-l-md px-3 py-2"
                    />
                    <select
                      value={formData.amount_unit}
                      onChange={(e) =>
                        setFormData({ ...formData, amount_unit: e.target.value })
                      }
                      className="border border-gray-300 border-l-0 rounded-r-md px-3 py-2"
                    >
                      <option value="g">g</option>
                      <option value="ml">ml</option>
                      <option value="capsules">capsules</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vervaldatum *
                  </label>
                  <input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) =>
                      setFormData({ ...formData, expiry_date: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bereidingsdatum *
                  </label>
                  <input
                    type="date"
                    value={formData.preparation_date}
                    onChange={(e) =>
                      setFormData({ ...formData, preparation_date: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
              </div>

              {/* Patient/Prescriber Info (for personalized) */}
              {formData.preparation_type === "personalized" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Patiënt *
                    </label>
                    <input
                      ref={patientInputRef}
                      type="text"
                      value={formData.patient_name}
                      onChange={(e) => {
                        setFormData({ ...formData, patient_name: e.target.value });
                        setPatientSearch(e.target.value);
                        setShowPatientSuggestions(true);
                      }}
                      onFocus={() => setShowPatientSuggestions(true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Zoek of voeg toe..."
                    />
                    {showPatientSuggestions && filteredPatients.length > 0 && (
                      <div
                        ref={patientDropdownRef}
                        className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                      >
                        {filteredPatients.map((patient) => (
                          <button
                            key={patient.id}
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                patient_id: patient.id,
                                patient_name: patient.name,
                              });
                              setShowPatientSuggestions(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          >
                            {patient.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voorschrijver
                    </label>
                    <input
                      ref={prescriberInputRef}
                      type="text"
                      value={formData.prescriber_name}
                      onChange={(e) => {
                        setFormData({ ...formData, prescriber_name: e.target.value });
                        setPrescriberSearch(e.target.value);
                        setShowPrescriberSuggestions(true);
                      }}
                      onFocus={() => setShowPrescriberSuggestions(true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Zoek of voeg toe..."
                    />
                    {showPrescriberSuggestions && filteredPrescribers.length > 0 && (
                      <div
                        ref={prescriberDropdownRef}
                        className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                      >
                        {filteredPrescribers.map((prescriber) => (
                          <button
                            key={prescriber.id}
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                prescriber_id: prescriber.id,
                                prescriber_name: prescriber.name,
                              });
                              setShowPrescriberSuggestions(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          >
                            {prescriber.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Posologie
                    </label>
                    <textarea
                      value={formData.posology}
                      onChange={(e) =>
                        setFormData({ ...formData, posology: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      rows={2}
                    />
                  </div>
                </div>
              )}

              {/* Ingredients */}
              <div>
                <h3 className="text-lg font-medium mb-2">Ingrediënten</h3>
                <div className={`grid gap-2 mb-2 ${(formData.preparation_form === "capsules" || formData.preparation_form === "suppositoiries") ? "grid-cols-8" : "grid-cols-6"}`}>
                  <div className="col-span-2 relative">
                    <input
                      ref={ingredientInputRef}
                      type="text"
                      value={ingredientForm.ingredient_name}
                      onChange={(e) => {
                        setIngredientForm({ ...ingredientForm, ingredient_name: e.target.value });
                        setIngredientSearch(e.target.value);
                        setShowIngredientSuggestions(true);
                      }}
                      onFocus={() => setShowIngredientSuggestions(true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Ingrediënt *"
                    />
                    {showIngredientSuggestions && filteredIngredients.length > 0 && (
                      <div
                        ref={ingredientDropdownRef}
                        className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                      >
                        {filteredIngredients.map((ingredient) => (
                          <button
                            key={ingredient.id}
                            type="button"
                            onClick={() => {
                              setIngredientForm({
                                ...ingredientForm,
                                ingredient_id: ingredient.id,
                                ingredient_name: ingredient.name,
                              });
                              setShowIngredientSuggestions(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          >
                            {ingredient.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={ingredientForm.internal_lot_number}
                    onChange={(e) =>
                      setIngredientForm({
                        ...ingredientForm,
                        internal_lot_number: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Lotnummer *"
                  />
                  {/* Unit dose fields for capsules and suppositories */}
                  {(formData.preparation_form === "capsules" || formData.preparation_form === "suppositoiries") ? (
                    <>
                      <input
                        type="number"
                        step="0.00001"
                        value={ingredientForm.unit_dose}
                        onChange={(e) => {
                          const unitDose = e.target.value;
                          const numberOfUnits = ingredientForm.number_of_units;
                          let calculated = "";
                          if (unitDose && numberOfUnits) {
                            const calc = parseFloat(unitDose) * parseInt(numberOfUnits);
                            // Convert if needed
                            if (ingredientForm.unit_dose_unit === "mg" && ingredientForm.unit === "g") {
                              calculated = (calc / 1000).toString();
                            } else if (ingredientForm.unit_dose_unit === "g" && ingredientForm.unit === "mg") {
                              calculated = (calc * 1000).toString();
                            } else {
                              calculated = calc.toString();
                            }
                          }
                          setIngredientForm({
                            ...ingredientForm,
                            unit_dose: unitDose,
                            theoretical_amount: calculated,
                          });
                        }}
                        className="border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Eenheidsdosis *"
                      />
                      <select
                        value={ingredientForm.unit_dose_unit}
                        onChange={(e) => {
                          setIngredientForm({ ...ingredientForm, unit_dose_unit: e.target.value });
                        }}
                        className="border border-gray-300 rounded-md px-3 py-2"
                      >
                        <option value="mg">mg</option>
                        <option value="g">g</option>
                      </select>
                      <input
                        type="number"
                        value={ingredientForm.number_of_units}
                        onChange={(e) => {
                          const numberOfUnits = e.target.value;
                          const unitDose = ingredientForm.unit_dose;
                          let calculated = "";
                          if (unitDose && numberOfUnits) {
                            const calc = parseFloat(unitDose) * parseInt(numberOfUnits);
                            // Convert if needed
                            if (ingredientForm.unit_dose_unit === "mg" && ingredientForm.unit === "g") {
                              calculated = (calc / 1000).toString();
                            } else if (ingredientForm.unit_dose_unit === "g" && ingredientForm.unit === "mg") {
                              calculated = (calc * 1000).toString();
                            } else {
                              calculated = calc.toString();
                            }
                          }
                          setIngredientForm({
                            ...ingredientForm,
                            number_of_units: numberOfUnits,
                            theoretical_amount: calculated,
                          });
                        }}
                        className="border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Aantal *"
                      />
                      <input
                        type="number"
                        step="0.00001"
                        value={ingredientForm.theoretical_amount}
                        readOnly
                        className="border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
                        placeholder="Totaal (auto)"
                      />
                    </>
                  ) : (
                    <input
                      type="number"
                      step="0.00001"
                      value={ingredientForm.theoretical_amount}
                      onChange={(e) =>
                        setIngredientForm({
                          ...ingredientForm,
                          theoretical_amount: e.target.value,
                        })
                      }
                      className="border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Theoretisch *"
                    />
                  )}
                  <input
                    type="number"
                    step="0.00001"
                    value={ingredientForm.actual_amount}
                    onChange={(e) =>
                      setIngredientForm({
                        ...ingredientForm,
                        actual_amount: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Werkelijk *"
                  />
                  <div className="flex">
                    <select
                      value={ingredientForm.unit}
                      onChange={(e) =>
                        setIngredientForm({ ...ingredientForm, unit: e.target.value })
                      }
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="g">g</option>
                      <option value="mg">mg</option>
                      <option value="ml">ml</option>
                    </select>
                    <button
                      onClick={handleAddIngredient}
                      className="ml-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Ingredients List */}
                <div className="border border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Ingrediënt
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Lotnummer
                        </th>
                        {(formData.preparation_form === "capsules" || formData.preparation_form === "suppositoiries") && (
                          <>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Eenheidsdosis
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Aantal
                            </th>
                          </>
                        )}
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Theoretisch
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Werkelijk
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Actie
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {preparationIngredients.map((ing, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm">{ing.ingredient_name}</td>
                          <td className="px-4 py-2 text-sm">{ing.internal_lot_number}</td>
                          {(formData.preparation_form === "capsules" || formData.preparation_form === "suppositoiries") && (
                            <>
                              <td className="px-4 py-2 text-sm">
                                {ing.unit_dose ? `${ing.unit_dose} ${ing.unit_dose_unit || "mg"}` : "-"}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {ing.number_of_units || "-"}
                              </td>
                            </>
                          )}
                          <td className="px-4 py-2 text-sm">
                            {ing.theoretical_amount} {ing.unit}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {ing.actual_amount} {ing.unit}
                          </td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() => handleRemoveIngredient(index)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preparationIngredients.length === 0 && (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      Geen ingrediënten toegevoegd
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opmerkingen
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleSavePreparation}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompoundingPreparations;

